/**
 * MarketplacePage — the public `/marketplace` browse surface (STORY-003-005;
 * rebuilt 2026-08 onto the counter-slip direction).
 *
 * The default landing of the marketplace: a product grid driven by a filter
 * sidebar, no auth required. It consumes `GET /marketplace/search?mode=browse`
 * (STORY-003-009; the empty-q list-all path) via `useMarketplaceBrowse` and
 * renders `ProductGrid` + `CategoryFilterSidebar`.
 *
 * WHAT CHANGED VISUALLY. The page opened with a full-width white card holding
 * a title and a count and nothing else — ~130px of empty surface on desktop and
 * a fifth of the first viewport on a phone, spent restating the page the user
 * just navigated to. It is now a ruled `SlipHeader`: title, count and the
 * mobile filter trigger on one line, a drawn rule under them, and — the real
 * addition — an `AppliedFilters` row that names every active facet and can
 * remove each one. A filtered catalogue now says so on screen instead of only
 * in the query string.
 *
 * URL ⇄ state sync (unchanged): every filter is mirrored to the query string
 * with the story's SHORTHAND keys (`category`, `market` repeatable, `in_stock`,
 * `min`, `max`, `sort`) so the URL is sharable. On mount the query string is
 * parsed into the filter state; on a filter change the URL is updated via
 * `useSearchParams`. The page derives the IN-MEMORY `BrowseFilters` from the
 * URL and hands them to the query hook, which translates them to the REAL
 * backend param names at the API seam. Because the filters flow through the
 * query key, a filter change re-issues the query from page 1 and the grid is
 * REPLACED (not appended — AC #2); "Load more" appends via the infinite query's
 * `next_cursor` (AC #9).
 *
 * Responsive (AC #8): at ≥1024px the sidebar is a static column; below that it
 * collapses behind a "Filters" button that opens a slide-over Sheet. The grid is
 * single-column at 390px with no horizontal scroll.
 *
 * Analytics: `marketplace.browse.viewed` on mount, `filter_applied` on filter
 * change, `load_more` on pagination, `product.card_clicked` on a card click.
 *
 * Tokens via the `--g-*` Tailwind mirror; strings from the `marketplace`
 * namespace. No hand-rolled colors, no hardcoded copy. This page is public —
 * no auth header, no JWT, no PII.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";
import { overlayVariants, slideOverVariants } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";
import { AppliedFilters } from "@/features/marketplace/AppliedFilters";
import { CategoryFilterSidebar } from "@/features/marketplace/CategoryFilterSidebar";
import { EmptyState } from "@/features/marketplace/EmptyState";
import { LoadError } from "@/features/marketplace/LoadError";
import { ProductGrid } from "@/features/marketplace/ProductGrid";
import { SlipHeader } from "@/features/marketplace/SlipHeader";
import type { MarketOption } from "@/features/marketplace/MarketFilterMultiselect";
import type { PriceRangeValue } from "@/features/marketplace/PriceRangeInput";
import { isSortValue, type SortValue } from "@/features/marketplace/SortSelect";
import {
  flattenBrowsePages,
  useMarketplaceBrowse,
  type BrowseFilters,
  type ProductSearchItem,
} from "@/features/marketplace/useMarketplaceBrowse";
import {
  trackBrowseFilterApplied,
  trackBrowseLoadMore,
  trackBrowseViewed,
  trackProductCardClicked,
  type BrowseFilterSnapshot,
} from "@/features/marketplace/browse-analytics";

/** URL query-string keys (story shorthand — NOT the backend param names). */
const PARAM_CATEGORY = "category";
const PARAM_MARKET = "market";
const PARAM_IN_STOCK = "in_stock";
const PARAM_MIN = "min";
const PARAM_MAX = "max";
const PARAM_SORT = "sort";

/** Every facet key, for the one-tap "clear all". */
const ALL_FILTER_PARAMS = [
  PARAM_CATEGORY,
  PARAM_MARKET,
  PARAM_IN_STOCK,
  PARAM_MIN,
  PARAM_MAX,
  PARAM_SORT,
] as const;

/**
 * Static market options. There is no public markets-list endpoint in this
 * story's dependency set (only `/marketplace/categories` + `/marketplace/search`)
 * and inventing one is forbidden; the story specs a "static list of markets
 * (3–5 at MVP per PRD)". These ids are passed through to the backend
 * `market_id` filter verbatim. STORY-003-008 (search UI) may later promote this
 * to a real `/marketplace/markets` listing; until then it is intentionally
 * empty so the multiselect renders its "All markets" placeholder rather than
 * inventing seed ids.
 */
const STATIC_MARKET_OPTIONS: readonly MarketOption[] = [];

/** Parse a non-negative number from a URL string, or null when absent/invalid. */
function parseUrlPrice(raw: string | null): number | null {
  if (raw === null) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export interface ParsedBrowseUrl {
  categoryId: string | null;
  marketIds: string[];
  inStockOnly: boolean;
  priceRange: PriceRangeValue;
  sort: SortValue;
}

/** Parse the current URL search params into the in-memory filter state. */
export function parseBrowseUrl(params: URLSearchParams): ParsedBrowseUrl {
  const sortParam = params.get(PARAM_SORT);
  return {
    categoryId: params.get(PARAM_CATEGORY),
    marketIds: params.getAll(PARAM_MARKET),
    inStockOnly: params.get(PARAM_IN_STOCK) === "true",
    priceRange: {
      min: parseUrlPrice(params.get(PARAM_MIN)),
      max: parseUrlPrice(params.get(PARAM_MAX)),
    },
    sort: isSortValue(sortParam) ? sortParam : "relevance",
  };
}

/** Build a PII-free analytics snapshot from the parsed filter state. */
function toSnapshot(parsed: ParsedBrowseUrl): BrowseFilterSnapshot {
  return {
    hasCategory: parsed.categoryId !== null,
    marketCount: parsed.marketIds.length,
    inStockOnly: parsed.inStockOnly,
    hasPriceMin: parsed.priceRange.min !== null,
    hasPriceMax: parsed.priceRange.max !== null,
    sort: parsed.sort,
  };
}

/** Focusable descendants, in DOM order, for the Sheet's focus trap. */
const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  /** The filter sidebar to host inside the panel (one of the two instances). */
  children: ReactElement;
}

/**
 * FilterSheet — the mobile filter slide-over, shared by MarketplacePage and
 * SearchResultsPage. A hand-rolled accessible dialog (no @radix dependency,
 * consistent with HeaderSearchBar):
 *
 *  - AnimatePresence so the panel slides out before unmount; the overlay fades
 *    and the panel slides in from the left on the only non-easeOut curve. Exit
 *    runs short (<=200ms) and, under prefers-reduced-motion, the whole dialog
 *    mounts/unmounts instantly so e2e close assertions stay synchronous.
 *  - Focus management: on open, focus moves to the close button; Tab is trapped
 *    within the panel; Escape closes; on close, focus returns to the trigger
 *    that opened it (the caller keeps that ref). The overlay is click-to-close.
 *
 * 2026-08: the panel's title bar is now a ruled slip header that STICKS to the
 * top of the scroll — a five-facet panel scrolls on a 390px phone, and losing
 * the close button off the top of a modal is how a user gets stranded. The
 * scrim uses `--g-scrim` (which is theme-aware) instead of a literal black/40.
 */
export function FilterSheet({
  open,
  onClose,
  children,
}: FilterSheetProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const { reduced } = useGardenMotion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const headingId = useId();

  // Move focus into the panel on open; Escape closes; Tab is trapped. Focus
  // restoration to the opener is owned by the caller (it controls `open`).
  useEffect(() => {
    if (!open) return;
    // Focus the close button on the next frame (after the panel mounts).
    const raf = window.requestAnimationFrame(() => closeRef.current?.focus());

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (panel === null) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          data-testid="filters-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
        >
          <motion.div
            className="absolute inset-0 bg-[color:var(--g-scrim)]"
            data-testid="filters-sheet-overlay"
            onClick={onClose}
            aria-hidden="true"
            variants={reduced ? undefined : overlayVariants}
            initial={reduced ? false : "hidden"}
            animate={reduced ? undefined : "show"}
            exit={reduced ? undefined : "exit"}
          />
          <motion.div
            ref={panelRef}
            className="absolute inset-y-0 left-0 flex w-[88%] max-w-sm flex-col overflow-y-auto bg-card text-card-foreground shadow-[var(--g-shadow-xl)]"
            variants={reduced ? undefined : slideOverVariants("x", -16)}
            initial={reduced ? false : "hidden"}
            animate={reduced ? undefined : "show"}
            exit={reduced ? undefined : "exit"}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-[color:var(--g-rule-strong)] bg-card px-4 py-3">
              <h2
                id={headingId}
                className="font-display text-base font-bold tracking-tight"
              >
                {t("filters.title")}
              </h2>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                data-testid="filters-close"
                aria-label={t("filters.close")}
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-[10px] text-[color:var(--g-ink-2)] transition-colors hover:bg-[color:var(--g-sunken)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <X {...iconProps("lg")} />
              </button>
            </div>
            <div className="px-4 py-4">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function MarketplacePage(): ReactElement {
  const { t } = useTranslation("marketplace");
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  // Restore focus to the trigger when the filter Sheet closes (a11y).
  const filtersTriggerRef = useRef<HTMLButtonElement | null>(null);
  const openSheet = useCallback((): void => setSheetOpen(true), []);
  const closeSheet = useCallback((): void => {
    setSheetOpen(false);
    filtersTriggerRef.current?.focus();
  }, []);

  const parsed = useMemo(() => parseBrowseUrl(searchParams), [searchParams]);

  const filters: BrowseFilters = useMemo(
    () => ({
      categoryId: parsed.categoryId,
      marketIds: parsed.marketIds,
      inStockOnly: parsed.inStockOnly,
      minPrice: parsed.priceRange.min,
      maxPrice: parsed.priceRange.max,
      sort: parsed.sort,
    }),
    [parsed],
  );

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMarketplaceBrowse(filters);

  const items: ProductSearchItem[] = useMemo(
    () => flattenBrowsePages(data),
    [data],
  );
  // SCRUM-184, extended to browse. `total_estimate` is the PLANNER's row guess
  // (EXPLAIN "Plan Rows"; Addendum §6.4 forbids COUNT(*) on the latency
  // budget), not a real count, and it routinely disagrees with reality — a
  // price filter matching nothing still reported "2 товара". The search page
  // already refused to print a count over an empty grid; browse did not, and
  // now that the count sits on the same rule as the title the contradiction
  // was impossible to miss. Same guard, same reason: never claim a number the
  // user can see is wrong.
  //
  // Unlike search, browse CAN reach exactness (ADR-050, garden-backlog/ADR-LOG.md):
  // BROWSE_SQL applies every filter inside the ordered, capped `page_ids` CTE
  // (repository.py:295-336), so `hasNextPage === false` genuinely means no
  // further matches exist and the flattened list IS the complete result set —
  // at zero extra query cost.
  const rawEstimate = data?.pages[0]?.total_estimate ?? null;
  const exactTotal = data !== undefined && !hasNextPage ? items.length : null;

  // `marketplace.browse.viewed` once on mount (NOT on every filter change).
  const viewedRef = useRef<boolean>(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackBrowseViewed(toSnapshot(parsed));
    // Intentionally mount-only — the filter-applied event covers later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mutate one URL param + reset the sheet's transient state. Filter changes
  // flow through the query key, so the grid replaces (page 1) automatically.
  const applyParams = useCallback(
    (mutate: (next: URLSearchParams) => void): void => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next);
      trackBrowseFilterApplied(toSnapshot(parseBrowseUrl(next)));
    },
    [searchParams, setSearchParams],
  );

  const handleCategorySelect = useCallback(
    (categoryId: string): void => {
      // CategoryTree already wrote `?category=<id>` to the URL; emit the
      // filter-applied event for parity with the other controls.
      const next = new URLSearchParams(searchParams);
      next.set(PARAM_CATEGORY, categoryId);
      trackBrowseFilterApplied(toSnapshot(parseBrowseUrl(next)));
    },
    [searchParams],
  );

  const handleCategoryReset = useCallback((): void => {
    applyParams((next) => next.delete(PARAM_CATEGORY));
  }, [applyParams]);

  const handleMarketChange = useCallback(
    (ids: string[]): void => {
      applyParams((next) => {
        next.delete(PARAM_MARKET);
        for (const id of ids) next.append(PARAM_MARKET, id);
      });
    },
    [applyParams],
  );

  const handleInStockChange = useCallback(
    (checked: boolean): void => {
      applyParams((next) => {
        if (checked) next.set(PARAM_IN_STOCK, "true");
        else next.delete(PARAM_IN_STOCK);
      });
    },
    [applyParams],
  );

  const handlePriceChange = useCallback(
    (value: PriceRangeValue): void => {
      applyParams((next) => {
        if (value.min !== null) next.set(PARAM_MIN, String(value.min));
        else next.delete(PARAM_MIN);
        if (value.max !== null) next.set(PARAM_MAX, String(value.max));
        else next.delete(PARAM_MAX);
      });
    },
    [applyParams],
  );

  const handleSortChange = useCallback(
    (value: SortValue): void => {
      applyParams((next) => {
        if (value === "relevance") next.delete(PARAM_SORT);
        else next.set(PARAM_SORT, value);
      });
    },
    [applyParams],
  );

  /** One tap back to the whole catalogue. */
  const handleClearAll = useCallback((): void => {
    applyParams((next) => {
      for (const key of ALL_FILTER_PARAMS) next.delete(key);
    });
  }, [applyParams]);

  const handleCardClick = useCallback(
    (product: ProductSearchItem, position: number): void => {
      trackProductCardClicked(product.id, product.seller_id, position);
    },
    [],
  );

  const handleLoadMore = useCallback((): void => {
    trackBrowseLoadMore();
    void fetchNextPage();
  }, [fetchNextPage]);

  const handleRetry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  // A failed catalogue fetch also raises a toast that CARRIES the retry — the
  // inline slip can be off-screen after a "load more", and an error the user
  // cannot act on from where they are is a dead end. `id` collapses repeats
  // onto one toast instead of stacking one per refetch.
  useEffect(() => {
    if (!isError) return;
    notify.error(t("browse.loadError"), {
      id: "marketplace-browse-error",
      description: t("browse.loadErrorHint"),
      action: { label: t("browse.retry"), onClick: handleRetry },
    });
  }, [isError, t, handleRetry]);

  // The sidebar renders in two places (desktop column + mobile Sheet); each
  // instance gets a DISTINCT aria-label so the two `<aside>` landmarks are
  // individually distinguishable (axe landmark-unique). Only one is visible at a
  // time via the responsive classes; both may be in the DOM when the Sheet is
  // open at a narrow width.
  const renderSidebar = (ariaLabel: string): ReactElement => (
    <CategoryFilterSidebar
      ariaLabel={ariaLabel}
      marketOptions={STATIC_MARKET_OPTIONS}
      selectedMarketIds={parsed.marketIds}
      onMarketChange={handleMarketChange}
      inStockOnly={parsed.inStockOnly}
      onInStockChange={handleInStockChange}
      priceRange={parsed.priceRange}
      onPriceChange={handlePriceChange}
      sort={parsed.sort}
      onSortChange={handleSortChange}
      activeCategoryId={parsed.categoryId}
      onCategorySelect={handleCategorySelect}
      onCategoryReset={handleCategoryReset}
    />
  );

  const showEmpty = !isLoading && !isError && items.length === 0;

  return (
    <main className="mx-auto flex w-full max-w-screen-xl flex-col gap-5 px-4 py-5 sm:py-6">
      <SlipHeader
        title={t("browse.title")}
        count={
          items.length === 0
            ? null // SCRUM-184 zero-mask — unchanged
            : exactTotal !== null
              ? t("browse.resultCount", { count: exactTotal })
              : rawEstimate !== null
                ? t("browse.resultCountApprox", {
                    // A further page exists ⇒ the true total is >= items + 1.
                    count: Math.max(rawEstimate, items.length + 1),
                  })
                : null
        }
        countTestId="result-count"
        actions={
          // Mobile-only "Filters" trigger (≥lg the static sidebar is shown).
          <button
            ref={filtersTriggerRef}
            type="button"
            onClick={openSheet}
            data-testid="filters-open"
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-[10px] border border-[color:var(--g-rule-strong)] px-3 text-sm font-semibold text-foreground transition-colors hover:border-[color:var(--g-primary)] hover:text-[color:var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:hidden"
          >
            <SlidersHorizontal {...iconProps("sm")} />
            {t("filters.open")}
          </button>
        }
      >
        <AppliedFilters
          categoryId={parsed.categoryId}
          onClearCategory={handleCategoryReset}
          marketOptions={STATIC_MARKET_OPTIONS}
          marketIds={parsed.marketIds}
          onMarketChange={handleMarketChange}
          inStockOnly={parsed.inStockOnly}
          onInStockChange={handleInStockChange}
          priceRange={parsed.priceRange}
          onPriceChange={handlePriceChange}
          sort={parsed.sort}
          onSortChange={handleSortChange}
          onClearAll={handleClearAll}
        />
      </SlipHeader>

      <div className="flex gap-8">
        {/* Static desktop sidebar. Sticky so the facets stay reachable while a
            400-product grid scrolls past them. */}
        <div
          className="hidden w-60 shrink-0 lg:block xl:w-64"
          data-testid="sidebar-desktop"
        >
          <div className="sticky top-4">{renderSidebar(t("aria.filters"))}</div>
        </div>

        <div className="min-w-0 flex-1">
          {isError ? (
            <LoadError
              message={t("browse.loadError")}
              onRetry={handleRetry}
              testId="browse-error"
              retryTestId="browse-retry"
            />
          ) : showEmpty ? (
            <EmptyState />
          ) : (
            <>
              <ProductGrid
                items={items}
                isLoading={isLoading}
                onCardClick={handleCardClick}
                entranceKey="browse"
              />
              {hasNextPage && (
                <div className="mt-8 flex justify-center border-t border-[color:var(--g-rule)] pt-6">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isFetchingNextPage}
                    data-testid="load-more"
                    className="inline-flex min-h-[44px] items-center rounded-[10px] border border-[color:var(--g-rule-strong)] px-6 text-sm font-semibold text-foreground transition-colors hover:border-[color:var(--g-primary)] hover:text-[color:var(--g-primary)] disabled:pointer-events-none disabled:text-[color:var(--g-ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {isFetchingNextPage
                      ? t("browse.loadingMore")
                      : t("browse.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <FilterSheet open={sheetOpen} onClose={closeSheet}>
        {renderSidebar(t("filters.title"))}
      </FilterSheet>
    </main>
  );
}

export default MarketplacePage;
